import { doseIndex, items, sessionFor, sessionKeyFor } from "./plan";
import { isTwoSided } from "./exerciseMeta";
import { moveById } from "./moves";
import { trainingFlags, type FillOptions } from "./programme";
import { isSteadyKey } from "./templates";
import type { BlockItem, DayRan, KayaState, PhaseIndex, QueueStep, RestStep, SessionKey, WorkStep } from "./types";

/** The rest between a seated move and a standing one: at least this long, and named (ADR-052). */
export const STAND_UP_SECS = 20;
export const STAND_UP_LABEL = "Stand up slowly";
/** The breather between two blocks. */
export const BLOCK_REST_SECS = 30;

function standUp(rest: RestStep): RestStep {
  return { ...rest, secs: Math.max(STAND_UP_SECS, rest.secs), label: STAND_UP_LABEL };
}

/** Flattens a session into the linear step list the player walks through:
 *  work steps (split left/right for `side` moves) interleaved with rests. A Steady key is its
 *  template filled for the person (ADR-052); `opts` carries the phone's answers, a frozen day's
 *  flags, or the short version. */
export function buildQueue(S: KayaState, key: SessionKey, p: PhaseIndex, opts?: FillOptions): QueueStep[] {
  const s = sessionFor(key, S, opts);
  const q: QueueStep[] = [];

  s.blocks.forEach((b, bi) => {
    const rounds = b.rounds[p];
    // A move this build does not know is left out rather than thrown on (ADR-051).
    const its: BlockItem[] = items(S, b).filter((it) => moveById(it.id));

    for (let r = 1; r <= rounds; r++) {
      its.forEach((it, ii) => {
        const ex = moveById(it.id);
        if (!ex) return;
        const base = {
          ex,
          block: b.title,
          round: r,
          rounds,
          idx: ii + 1,
          total: its.length,
          bi,
          itemId: it.id,
          ...(it.cue ? { cue: it.cue } : {}),
          ...(it.note ? { note: it.note } : {}),
        };

        const twoSided = isTwoSided(it.id, it.side);

        if (it.t) {
          if (twoSided) {
            q.push({ kind: "time", secs: it.t[p], sideLabel: "left side", ...base });
            q.push({ kind: "time", secs: it.t[p], sideLabel: "right side", ...base });
          } else {
            q.push({ kind: "time", secs: it.t[p], ...base });
          }
        } else {
          q.push({ kind: "reps", reps: it.r![p], side: twoSided, ...base });
        }

        const last = ii === its.length - 1;
        if (!last) {
          const rest: RestStep = {
            kind: "rest",
            secs: S.profile.restMove || b.rest[p],
            block: b.title,
            round: r,
            rounds,
            bi,
          };
          q.push(its[ii + 1].standUp ? standUp(rest) : rest);
        } else if (r < rounds) {
          const rest: RestStep = {
            kind: "rest",
            secs: S.profile.restRound || b.restRound || b.rest[p],
            block: b.title,
            roundEnd: true,
            round: r,
            rounds,
            bi,
          };
          q.push(its[0].standUp ? standUp(rest) : rest);
        }
      });
    }

    if (bi < s.blocks.length - 1) {
      const rest: RestStep = { kind: "rest", secs: BLOCK_REST_SECS, block: b.title, blockEnd: true };
      q.push(s.blocks[bi + 1].standUp ? standUp(rest) : rest);
    }
  });

  return q;
}

/**
 * The round a rest step shows. A rest inside a block carries its own; the breather between two
 * blocks is queued without one, so it takes the round of the move it leads into — the player
 * once printed "undefined/undefined" there — and, after the last block, the one it followed.
 * Null only when the queue has no work at all.
 */
export function restRound(queue: QueueStep[], i: number): { round: number; rounds: number } | null {
  const st = queue[i];
  if (!st || st.kind !== "rest") return null;
  if (st.round !== undefined && st.rounds !== undefined) return { round: st.round, rounds: st.rounds };
  for (let j = i + 1; j < queue.length; j++) {
    const q = queue[j];
    if (q.kind !== "rest") return { round: q.round, rounds: q.rounds };
  }
  for (let j = i - 1; j >= 0; j--) {
    const q = queue[j];
    if (q.kind !== "rest") return { round: q.round, rounds: q.rounds };
  }
  return null;
}

/** Estimated duration of one step. Reps have no timer, so they get a nominal 3 s each
 *  (6 s when the move is performed on both sides). The get-set lead-in is deliberately not
 *  counted: it is time spent kneeling down, not time spent working, and this feeds the
 *  calorie estimate. */
export function stepSecs(st: QueueStep): number {
  if (st.kind === "rest") return st.secs;
  if (st.kind === "time") return st.secs;
  return st.reps * (st.side ? 6 : 3);
}

export const DEFAULT_PREP_SECS = 10;
export const MAX_PREP_SECS = 30;
/** The Simple player's floor on the get-set (plan §3.7). */
export const SIMPLE_PREP_SECS = 20;

/**
 * Seconds of "get set" before a timed move's clock starts.
 *
 * Without it the timer began the instant Start was pressed, so a move that needs you kneeling on
 * a cushion or lying on your side lost its first several seconds to getting there — or had to be
 * set up before pressing Start, with the phone out of reach. Reps are unaffected: they have no
 * clock to be late for.
 */
export function prepSecs(S: KayaState, simple = false): number {
  const n = S.profile.prepSecs;
  const set = typeof n !== "number" || !Number.isFinite(n) || n < 0 ? DEFAULT_PREP_SECS : Math.min(MAX_PREP_SECS, Math.round(n));
  // Ten seconds reads the name; it does not get an older body up from a chair. (ADR-055)
  return simple ? Math.max(SIMPLE_PREP_SECS, set) : set;
}

/**
 * The real length of a session, in whole minutes: every step `stepSecs` prices plus the get-set
 * lead-in before each timed move. The generated `mins` is the reference's label — A says 40 and
 * runs 45 to 59 — and is kept only because generated data is never edited. (ADR-050)
 *
 * It lives here rather than in plan.ts because plan.ts is what this module builds on.
 */
export function sessionMinutes(S: KayaState, key: SessionKey, p: PhaseIndex, opts?: FillOptions, simple?: boolean): number {
  return queueMinutes(S, buildQueue(S, key, p, opts), simple);
}

/**
 * `sessionMinutes` for a queue already built. `simple` is the phone's player mode
 * (`playerView.ts::playerMode`): the Simple player's get-set is 20 s, so Train and its start screen
 * agree on the length. Without it, the mode a linked owner set in the profile.
 */
export function queueMinutes(S: KayaState, q: QueueStep[], simple?: boolean): number {
  const lead = prepSecs(S, simple ?? S.profile.training?.simple === true);
  const secs = q.reduce((a, st) => a + stepSecs(st) + (st.kind === "time" ? lead : 0), 0);
  return Math.round(secs / 60);
}

/**
 * A short, stable hash of the steps in queue order (FNV-1a over the move ids, the side and a
 * mark per rest). Equal signatures mean tick `i` names the same step in both queues; doses and
 * rest lengths are left out because they move with the phase without moving any index.
 */
export function queueSignature(q: QueueStep[]): string {
  let h = 0x811c9dc5;
  const text = q
    .map((st) => (st.kind === "rest" ? "~" : st.itemId + (st.kind === "time" && st.sideLabel ? ":" + st.sideLabel[0] : "")))
    .join("|");
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/** What a day starting `key` now would freeze: today's phase and hip-safe, and the queue they make.
 *  A Steady template also freezes the flags it was filled with — the phone's answers among them —
 *  and whether it ran short (ADR-052). */
export function ranFor(S: KayaState, key: SessionKey, now = Date.now(), opts: FillOptions = {}): DayRan {
  const p = doseIndex(S, key, opts.ds);
  const steady = isSteadyKey(key);
  const flags = steady ? (opts.flags ? [...opts.flags] : trainingFlags(S, opts.device, opts.ds)) : undefined;
  const q = buildQueue(S, key, p, steady ? { flags, short: opts.short } : undefined);
  return {
    key,
    phase: p,
    hipSafe: !!S.profile.hipSafe,
    sig: queueSignature(q),
    n: q.filter((st) => st.kind !== "rest").length,
    mask: q.map((st) => (st.kind === "rest" ? "0" : "1")).join(""),
    at: now,
    ...(flags ? { flags } : {}),
    ...(steady && opts.short ? { short: true } : {}),
  };
}

/** The queue a frozen block describes, rebuilt with its own phase, hip-safe and flags. */
export function ranQueue(S: KayaState, ran: DayRan): QueueStep[] {
  const S2 = !!S.profile.hipSafe === ran.hipSafe ? S : { ...S, profile: { ...S.profile, hipSafe: ran.hipSafe } };
  return buildQueue(S2, ran.key, ran.phase, ran.flags || ran.short ? { flags: ran.flags, short: ran.short } : undefined);
}

/**
 * The queue a day's ticks are read against. A day that froze what it ran is rebuilt from that
 * block, and trusted only when the rebuilt signature matches; when the session's step list has
 * changed since, the answer is the frozen count alone (`queue: null`), because re-indexing the
 * ticks against the new list would credit other moves. A day with no block — every day before
 * ADR-050, and today before its first tick — is rebuilt from today's settings, as it always was.
 */
export function dayQueue(
  S: KayaState,
  ds: string,
  key: SessionKey
): { queue: QueueStep[]; ran: DayRan | null } | { queue: null; ran: DayRan } {
  const ran = S.logs[ds]?.ran;
  if (ran && ran.key === key) {
    const q = ranQueue(S, ran);
    if (queueSignature(q) === ran.sig) return { queue: q, ran };
    return { queue: null, ran };
  }
  return { queue: buildQueue(S, key, doseIndex(S, key, ds)), ran: null };
}

/** Work steps ticked off out of the session's, or null for a session with no work. */
export function dayProgress(S: KayaState, ds: string, key: SessionKey): { done: number; total: number } | null {
  const ticks = S.logs[ds]?.done?.[key] ?? {};
  const dq = dayQueue(S, ds, key);
  if (dq.queue === null) {
    const { n, mask } = dq.ran;
    if (!n) return null;
    let done = 0;
    for (const k of Object.keys(ticks)) if (!mask || mask[+k] === "1") done++;
    return { done: Math.min(done, n), total: n };
  }
  let total = 0;
  let done = 0;
  dq.queue.forEach((st, i) => {
    if (st.kind === "rest") return;
    total++;
    if (ticks[i]) done++;
  });
  return total ? { done, total } : null;
}

/** Fraction of the work steps ticked off for a day, or null when the session has no work. */
export function sessionPct(S: KayaState, date: string, key?: SessionKey): number | null {
  const pr = dayProgress(S, date, key || sessionKeyFor(S, date));
  return pr ? pr.done / pr.total : null;
}

/** Storage bucket for `done` flags: the plain session, a rescue session, or an extra one. */
export function storeKey(key: SessionKey, opts?: { rescue?: boolean; extra?: boolean }): string {
  if (opts?.rescue) return "rescue";
  if (opts?.extra) return "extra:" + key;
  return key;
}
